/**
 * My Referral Page
 *
 * Users can view and share their referral code and see who joined with it.
 * Referrals carry NO reward in BeHeard (no credits are granted: credits are
 * Central's, and a referral reward is a future Central capability), so the
 * page promises none and shows none. A query that fails shows an error with
 * a retry — never zeros.
 * Route: /referrals
 */

import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import {
  useGetMyReferralCodeQuery,
  useGetMyReferralStatsQuery,
  useGetMyRedemptionsQuery,
  useGetReferralLimitsQuery,
} from '../store/api/referralApi';

export function MyReferralPage() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const {
    data: myCode,
    isLoading: isLoadingCode,
    isError: isCodeError,
    refetch: refetchCode,
  } = useGetMyReferralCodeQuery(undefined, {
    skip: !isAuthenticated,
  });
  const {
    data: stats,
    isLoading: isLoadingStats,
    isError: isStatsError,
    refetch: refetchStats,
  } = useGetMyReferralStatsQuery(undefined, {
    skip: !isAuthenticated,
  });
  const {
    data: redemptionsData,
    isLoading: isLoadingRedemptions,
    isError: isRedemptionsError,
    refetch: refetchRedemptions,
  } = useGetMyRedemptionsQuery({ limit: 10 }, { skip: !isAuthenticated });
  const { data: limits, isError: isLimitsError } = useGetReferralLimitsQuery(undefined, {
    skip: !isAuthenticated,
  });

  if (!isAuthenticated) {
    navigate('/login?returnTo=/referrals');
    return null;
  }

  const isLoading = isLoadingCode || isLoadingStats;

  const handleCopyCode = () => {
    if (myCode?.code) {
      navigator.clipboard.writeText(myCode.code);
      // Could add a toast notification here
    }
  };

  const handleCopyLink = () => {
    if (myCode?.shareUrl) {
      navigator.clipboard.writeText(myCode.shareUrl);
      // Could add a toast notification here
    }
  };

  const handleShare = async () => {
    if (myCode?.shareUrl && navigator.share) {
      try {
        await navigator.share({
          title: 'Join BeHeard',
          text: 'Join me on BeHeard with my referral code.',
          url: myCode.shareUrl,
        });
      } catch (err) {
        // User cancelled or share failed
      }
    } else {
      handleCopyLink();
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Invite Friends</h1>
        <p className="mt-1 text-gray-500">
          Share your referral code and see who joined with it
        </p>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Referral Card */}
          <div className="lg:col-span-2 space-y-6">
            {/* Share Your Code Card */}
            <div className="card">
              <div className="card-header">
                <h2 className="font-semibold text-gray-900">Your Referral Code</h2>
              </div>
              <div className="card-body space-y-4">
                {isCodeError && (
                  <LoadError what="your referral code" onRetry={refetchCode} testId="referral-code-error" />
                )}
                {myCode && (
                  <>
                    {/* Code Display */}
                    <div className="bg-primary-50 rounded-lg p-4 text-center">
                      <p className="text-3xl font-bold text-primary-700 tracking-wider">
                        {myCode.code}
                      </p>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-wrap gap-3">
                      <button
                        onClick={handleCopyCode}
                        className="flex-1 btn-secondary flex items-center justify-center gap-2"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                        Copy Code
                      </button>
                      <button
                        onClick={handleCopyLink}
                        className="flex-1 btn-secondary flex items-center justify-center gap-2"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                        </svg>
                        Copy Link
                      </button>
                      <button
                        onClick={handleShare}
                        className="flex-1 btn-primary flex items-center justify-center gap-2"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                        </svg>
                        Share
                      </button>
                    </div>

                    {/* Share URL */}
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs text-gray-500 mb-1">Share Link</p>
                      <p className="text-sm text-gray-700 break-all">{myCode.shareUrl}</p>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* How it Works Card */}
            <div className="card">
              <div className="card-header">
                <h2 className="font-semibold text-gray-900">How It Works</h2>
              </div>
              <div className="card-body">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="text-center p-4">
                    <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <span className="text-xl font-bold text-primary-600">1</span>
                    </div>
                    <h3 className="font-medium text-gray-900">Share Your Code</h3>
                    <p className="text-sm text-gray-500 mt-1">
                      Send your unique referral code to friends
                    </p>
                  </div>
                  <div className="text-center p-4">
                    <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <span className="text-xl font-bold text-primary-600">2</span>
                    </div>
                    <h3 className="font-medium text-gray-900">They Sign Up</h3>
                    <p className="text-sm text-gray-500 mt-1">
                      They enter your code when creating an account
                    </p>
                  </div>
                  <div className="text-center p-4">
                    <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <span className="text-xl font-bold text-primary-600">3</span>
                    </div>
                    <h3 className="font-medium text-gray-900">See Who Joined</h3>
                    <p className="text-sm text-gray-500 mt-1">
                      Their signup shows up in your referrals below
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Referrals */}
            <div className="card">
              <div className="card-header flex justify-between items-center">
                <h2 className="font-semibold text-gray-900">Recent Referrals</h2>
                <button
                  onClick={() => {
                    refetchStats();
                    refetchRedemptions();
                  }}
                  className="text-sm text-primary-600 hover:text-primary-700"
                >
                  Refresh
                </button>
              </div>
              <div className="card-body">
                {isLoadingRedemptions ? (
                  <div className="text-center py-8 text-gray-500">Loading...</div>
                ) : isRedemptionsError ? (
                  <LoadError what="your referrals" onRetry={refetchRedemptions} testId="referral-redemptions-error" />
                ) : !redemptionsData?.redemptions.length ? (
                  <div className="text-center py-8 text-gray-500">
                    <svg className="w-12 h-12 mx-auto text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    <p>No referrals yet</p>
                    <p className="text-sm">Share your code to invite friends.</p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {redemptionsData.redemptions.map((redemption) => (
                      <div key={redemption.id} className="py-3 flex justify-between items-center">
                        <div>
                          <p className="font-medium text-gray-900">
                            {redemption.newUser
                              ? `${redemption.newUser.firstName} ${redemption.newUser.lastName}`
                              : 'Anonymous User'}
                          </p>
                          <p className="text-sm text-gray-500">
                            {new Date(redemption.redeemedAt).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="text-right">
                          <span
                            className={`badge ${
                              redemption.status === 'completed'
                                ? 'badge-success'
                                : redemption.status === 'pending'
                                ? 'badge-warning'
                                : 'badge-error'
                            }`}
                          >
                            {redemption.status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar Stats */}
          <div className="space-y-6">
            {/* Stats Card */}
            <div className="card">
              <div className="card-header">
                <h2 className="font-semibold text-gray-900">Your Stats</h2>
              </div>
              <div className="card-body space-y-4">
                {isStatsError || !stats ? (
                  <LoadError what="your referral stats" onRetry={refetchStats} testId="referral-stats-error" />
                ) : (
                  <div className="grid grid-cols-3 gap-4" data-testid="referral-stats">
                    <div className="bg-blue-50 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-blue-700">{stats.totalReferrals}</p>
                      <p className="text-sm text-blue-600">Total</p>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-purple-700">{stats.successfulReferrals}</p>
                      <p className="text-sm text-purple-600">Joined</p>
                    </div>
                    <div className="bg-amber-50 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-amber-700">{stats.pendingReferrals}</p>
                      <p className="text-sm text-amber-600">Pending</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Monthly referrals against the anti-abuse cap (nothing is earned) */}
            {limits && !isLimitsError && (
              <div className="card">
                <div className="card-header">
                  <h2 className="font-semibold text-gray-900">This Month</h2>
                </div>
                <div className="card-body space-y-3">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-600">Referrals</span>
                      <span className="text-gray-900">
                        {limits.referralsThisMonth} / {limits.monthlyReferralCap}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-primary-500 h-2 rounded-full"
                        style={{
                          width: `${Math.min(100, (limits.referralsThisMonth / Math.max(1, limits.monthlyReferralCap)) * 100)}%`,
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            <p className="text-xs text-gray-500" data-testid="referral-no-rewards">
              Referrals don't come with credits or rewards in BeHeard.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

/** A failed load says so and offers a retry — never a row of zeros. */
function LoadError({ what, onRetry, testId }: { what: string; onRetry: () => void; testId: string }) {
  return (
    <div className="text-center py-6" data-testid={testId}>
      <p className="text-sm text-red-700">We couldn't load {what}.</p>
      <button onClick={() => onRetry()} className="mt-2 btn-secondary text-sm">
        Try again
      </button>
    </div>
  );
}

export default MyReferralPage;
